import { useEffect, useRef, useState } from "react";
import { customerProfileFailure, updateConversationCustomerType } from '../../models/customer/conversationCustomerProfileModel';
import { fetchMiscOptions } from "../../helpers";
import { useMiscOptions } from '../../../../features/dropdowns/viewmodels/useMiscOptions';
import { updateCustomer, fetchWatiContactProfile, updateWatiContactProfile } from "../../models/shared/customersModel";
import { buildCustomerSavePayload } from "../../models/customer/customerDraftModel";
import { useCustomerAddresses } from "./useCustomerAddresses";


export function useCustomerDraft({ customer, conversationId, onReloadProfile, open, onSaved, onFieldChanged, closeOnTypeSaved, onClose, onTypeStart, message }) {
  const [draft, setDraft] = useState({});
  const [sourceOptions, setSourceOptions] = useState([]);
  const typeOptionsState = useMiscOptions('customer_type', draft.type);
  const typeOptions = typeOptionsState.options;
  const [emailError, setEmailError] = useState("");
  const [saving, setSaving] = useState(false);
  const addressState = useCustomerAddresses({ customer, onFieldChanged, message });
  useEffect(() => {
    if (!open || !customer) return;
    setDraft({
      customer_name: customer.customer_name || "",
      customer_real_name: customer.customer_real_name || "",
      customer_email: customer.customer_email || "",
      customer_mobile: customer.customer_mobile || "",
      source: customer.source ?? null,
      status: customer.status ? true : false,
      type: customer.type ?? null,
    });

    addressState.resetAddresses();
  }, [open, customer]); // eslint-disable-line
  useEffect(() => {
    if (sourceOptions.length > 0) return;
    fetchMiscOptions("query_source").then(setSourceOptions);
  }, []); // eslint-disable-line



  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));
  // Split out from handleSave so the outer unsaved-changes guard (see
  // ChatConversation's "Save & Continue") can drive a save
  // without also triggering this drawer's own onClose — it decides on its
  // own whether to cascade-close afterward.
  const buildSavePayload = () => buildCustomerSavePayload(customer, draft);
  // WATI's own conversation search only matches its own stored contact name
  // (or phone) — it has no idea customer_real_name/customer_name exist. Without
  // this, renaming a customer in SRM makes them unfindable by that name in the
  // chat sidebar search, especially for hidden-number contacts with no phone
  // to fall back on. Best-effort: the SRM save has already succeeded by the
  // time this runs, so a WATI-side failure only warns, never rolls anything back.
  const syncNameToWati = async (name) => {
    if (!customer?.wati_contact_id || !name) return;
    try {
      const profile = await fetchWatiContactProfile(customer.customer_id);
      if (!profile) throw new Error("Could not load the WhatsApp contact profile");
      const res = await updateWatiContactProfile(customer.customer_id, { ...profile, firstName: name, fullName: name });
      if (!res.success) throw new Error(res.message);
    } catch {
      message.warning({
        content: "Saved in Uzdpart360, but couldn't sync the name to WhatsApp — searching for this customer by name there may still show the old one.",
        duration: 5,
      });
    }
  };

  // Returns "saved" | "nothing" | "error" — the outer guard treats "nothing"
  // the same as "saved" (safe to proceed closing) but keeps the drawer open
  // on "error" so the problem (e.g. the email validation below) is visible.
  const saveCustomer = async () => {
    if (!customer?.customer_id) return "error";
    if (emailError) { message.warning("Please fix the email address before saving"); return "error"; }
    const payload = buildSavePayload();
    if (Object.keys(payload).length === 1) {
      message.info("No changes to save");
      return "nothing";
    }
    setSaving(true);
    try {
      const res = await updateCustomer(payload);
      if (!res.success) throw new Error(res.message);
      message.success({ content: "Customer updated", duration: 2 });
      onSaved({ ...(res.data || {}), type: draft.type });
      if (payload.customer_name !== undefined || payload.customer_real_name !== undefined) {
        const newPrimaryName = (draft.customer_real_name || "").trim() || (draft.customer_name || "").trim() || null;
        syncNameToWati(newPrimaryName);
      }
      return "saved";
    } catch (e) {
      message.error(e.message || "Failed to save customer");
      return "error";
    } finally {
      setSaving(false);
    }
  };


  // Drives both the outer "unsaved changes" guard and the footer Save
  // button's enabled state — true only when there's something the Save
  // button itself would actually submit (source and type autosave
  // separately, so they don't count here).
  const topLevelDirty = !!customer?.customer_id && Object.keys(buildSavePayload()).length > 1;
  const [sourceSaving, setSourceSaving] = useState(false);

  const handleSourceChange = async (val) => {
    const newSource = val ?? null;
    setField("source", newSource);
    if (!customer?.customer_id) return;
    setSourceSaving(true);
    try {
      const res = await updateCustomer({ customer_id: customer.customer_id, source: newSource });
      if (!res.success) throw new Error(res.message);
      message.success({ content: "Source updated", duration: 2 });
      onFieldChanged?.({ customer_id: customer.customer_id, source: newSource });
    } catch (e) {
      message.error(e.message || "Failed to update source");
    } finally {
      setSourceSaving(false);
    }
  };

  const [typeSaving, setTypeSaving] = useState(false);
  const [typeError, setTypeError] = useState(null);
  const typeRequest = useRef(null);
  const lastType = useRef(null);
  const scope = JSON.stringify([customer?.customer_id, conversationId, open]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => { setTypeError(null); setTypeSaving(false); typeRequest.current = null; }, [scope]);

  const handleTypeChange = async (val) => {
    if (typeRequest.current?.scope === scope) return;
    const newType = val ?? null;
    setField("type", newType);
    onTypeStart?.();
    lastType.current = newType;
    if (!customer?.customer_id) {
      setField('type', customer?.type ?? null);
      const failure = { status: 'no_identity', message: 'No customer details for this chat', retryable: false };
      setTypeError(failure); message.error(failure.message); return;
    }
    const ticket = { scope };
    typeRequest.current = ticket;
    setTypeError(null);
    setTypeSaving(true);
    try {
      const res = conversationId ? await updateConversationCustomerType(conversationId, newType)
        : await updateCustomer({ customer_id: customer.customer_id, type: newType });
      if (currentScope.current !== scope) return;
      if (!conversationId && res?.success !== true) throw new Error(res?.message);
      message.success({ content: "Customer type updated", duration: 2 });
      const saved = conversationId ? res.customer : { customer_id: customer.customer_id, type: newType };
      setField('type', saved.type ?? null);
      onFieldChanged?.(saved);
      // Only auto-close when the drawer was opened specifically via the
      // header's "Set Now" button — that flow exists purely to set the
      // type, so once it's set there's nothing left to do here. A drawer
      // opened by clicking the avatar directly may have been opened for
      // some other reason, so it stays open even after the type is set.
      if (closeOnTypeSaved) onClose();
    } catch (e) {
      if (currentScope.current !== scope) return;
      setField('type', customer.type ?? null);
      const failure = conversationId ? customerProfileFailure(e) : { status: 'error', message: e.message || 'Failed to save customer type', retryable: true };
      setTypeError(failure);
      message.error(failure.message);
    } finally {
      if (typeRequest.current === ticket) { typeRequest.current = null; setTypeSaving(false); }
    }
  };

  const [copyingWatiName, setCopyingWatiName] = useState(false);

  const copyWatiName = async () => {
    if (!customer?.customer_id || !draft.customer_name?.trim()) return;
    const nameToCopy = draft.customer_name;
    setField("customer_real_name", nameToCopy);
    setCopyingWatiName(true);
    try {
      const res = await updateCustomer({ customer_id: customer.customer_id, customer_real_name: nameToCopy });
      if (!res.success) throw new Error(res.message);
      message.success({ content: "Real name updated", duration: 2 });
      onSaved({ ...(res.data || {}), type: draft.type });
    } catch (e) {
      message.error(e.message || "Failed to update real name");
    } finally {
      setCopyingWatiName(false);
    }
  };


  return { draft, setField, sourceOptions, typeOptions, typeOptionsState, emailError, setEmailError, saving, saveCustomer,
    topLevelDirty, sourceSaving, handleSourceChange, typeSaving, typeError, retryType: () => {
      if (conversationId && typeError?.status === 'error' && onReloadProfile) { setTypeError(null); onReloadProfile(); }
      else return handleTypeChange(lastType.current);
    }, handleTypeChange, copyingWatiName, copyWatiName, ...addressState };
}
